import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Tag, Loader2, Calendar, Store, User as UserIcon, ChevronLeft, ChevronRight, Sparkles, Trash2, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

const formatPhp = (n) => `₱${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso || '—'; }
};

/**
 * Discount Claims log — shared by Member and Merchant dashboards.
 *  mode='member'  → GET /api/services/my-claims, columns: Date | Merchant | Item | Member Price | Saved
 *  mode='merchant'→ GET /api/merchant/discount-claims, columns: Date | Member | Item | Member Price
 */
export function DiscountClaimsCard({ mode = 'member', allowDelete = false }) {
  const { api } = useAuth();
  const [data, setData] = useState({ claims: [], total: 0, total_savings: 0, total_revenue: 0, total_claims: 0 });
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const limit = 10;

  const endpoint = mode === 'merchant' ? '/api/merchant/discount-claims' : '/api/services/my-claims';

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`${endpoint}?skip=${skip}&limit=${limit}`);
      setData(d);
    } catch (e) { /* keep last data */ }
    setLoading(false);
  }, [api, endpoint, skip]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async (claimId) => {
    if (!window.confirm('Remove this claim from your list? This cannot be undone.')) return;
    setBusyId(claimId);
    try {
      await api.delete(`/api/services/my-claims/${claimId}`);
      toast.success('Discount claim removed');
      // Refetch — adjust skip if last item on page was removed
      const remaining = (data.total || 1) - 1;
      if (skip > 0 && skip >= remaining) setSkip(Math.max(0, skip - limit));
      else fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove claim');
    } finally {
      setBusyId(null);
    }
  };

  const showDelete = allowDelete && mode === 'member';

  const handleReclaim = async (claimId) => {
    setBusyId(claimId);
    try {
      const res = await api.post(`/api/services/my-claims/${claimId}/reclaim`);
      toast.success(`Re-claimed: ${res.data?.avail?.service_name || 'discount recorded'}`);
      setSkip(0);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to re-claim discount');
    } finally {
      setBusyId(null);
    }
  };

  const isMerchant = mode === 'merchant';
  const title = isMerchant ? 'Discount Claims (Member Foot-Traffic)' : 'My Discount Claims';
  const subtitle = isMerchant
    ? 'Members who have claimed your member discount. Use this log to honor the discount at the counter.'
    : 'Member discounts you have claimed at AGA partner merchants.';

  const pageStart = data.total ? skip + 1 : 0;
  const pageEnd = Math.min(skip + (data.claims?.length || 0), data.total);

  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid={`discount-claims-${mode}-card`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-[#FFB300]" />
              <h3 className="font-outfit text-lg font-semibold text-white">{title}</h3>
            </div>
            <p className="text-[#5C7599] text-xs mt-1 max-w-prose">{subtitle}</p>
          </div>
          {/* Aggregate badge */}
          {isMerchant ? (
            <div className="text-right">
              <p className="text-[#5C7599] text-xs">Total Claims · Revenue</p>
              <p className="text-white font-outfit text-lg font-semibold">
                {data.total_claims || 0} · {formatPhp(data.total_revenue)}
              </p>
            </div>
          ) : (data.total_savings > 0 && (
            <div className="text-right">
              <p className="text-[#5C7599] text-xs">Total Saved</p>
              <p className="text-[#00E676] font-outfit text-lg font-semibold flex items-center justify-end gap-1">
                <Sparkles className="w-4 h-4" /> {formatPhp(data.total_savings)}
              </p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-[#5C7599]" />
          </div>
        ) : data.claims?.length === 0 ? (
          <div className="py-10 text-center text-[#A0B4CC] text-sm">
            {isMerchant ? <Store className="w-10 h-10 mx-auto mb-2 opacity-40" /> : <Tag className="w-10 h-10 mx-auto mb-2 opacity-40" />}
            {isMerchant
              ? 'No discount claims yet. Once members start claiming your discount, they will appear here.'
              : "No discount claims yet. Visit the Marketplace and click \"Book\" or \"Purchase\" on a partner merchant's item to start saving."}
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#5C7599] text-[11px] uppercase tracking-wide">
                  <th className="p-2"><Calendar className="w-3 h-3 inline mr-1" /> Date</th>
                  <th className="p-2">{isMerchant ? <><UserIcon className="w-3 h-3 inline mr-1" /> Member</> : <><Store className="w-3 h-3 inline mr-1" /> Merchant</>}</th>
                  <th className="p-2">Item</th>
                  <th className="p-2 text-right">Member Price</th>
                  {!isMerchant && <th className="p-2 text-right">Saved</th>}
                  {!isMerchant && <th className="p-2 text-center">Status</th>}
                  {showDelete && <th className="p-2 text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {data.claims.map((c) => (
                  <tr key={c.id} className="border-t border-white/5" data-testid={`claim-row-${c.id}`}>
                    <td className="p-2 text-[#A0B4CC] whitespace-nowrap">{formatDate(c.created_at)}</td>
                    <td className="p-2 text-white">
                      {isMerchant ? (
                        <>
                          <div className="truncate max-w-[200px]">{c.member_name || c.member_email}</div>
                          {c.member_plan && <span className="text-[10px] text-[#5C7599]">{c.member_plan}</span>}
                        </>
                      ) : (
                        <span className="truncate inline-block max-w-[200px]">{c.merchant_name}</span>
                      )}
                    </td>
                    <td className="p-2 text-white">
                      <span className="truncate inline-block max-w-[220px]">{c.service_name}</span>
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${c.item_type === 'product' ? 'bg-[#00E676]/15 text-[#00E676]' : 'bg-[#00E5FF]/15 text-[#00E5FF]'}`}>
                        {c.item_type === 'product' ? 'Product' : 'Service'}
                      </span>
                    </td>
                    <td className="p-2 text-right text-white font-mono">{formatPhp(c.price_paid)}</td>
                    {!isMerchant && (
                      <td className="p-2 text-right font-mono text-[#00E676]">
                        {c.savings > 0 ? `+${formatPhp(c.savings)}` : '—'}
                      </td>
                    )}
                    {!isMerchant && (
                      <td className="p-2 text-center">
                        <span className="text-[10px] uppercase px-2 py-0.5 rounded bg-[#00E676]/15 text-[#00E676]">
                          {c.status || 'claimed'}
                        </span>
                      </td>
                    )}
                    {showDelete && (
                      <td className="p-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => handleReclaim(c.id)}
                          disabled={busyId === c.id}
                          className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-[#00B8D4] disabled:opacity-50 text-xs mr-3"
                          data-testid={`reclaim-${c.id}`}
                          title="Re-claim this discount now"
                        >
                          {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
                        </button>
                        <button
                          onClick={() => handleDelete(c.id)}
                          disabled={busyId === c.id}
                          className="inline-flex items-center gap-1 text-[#FF6B6B] hover:text-[#FF1744] disabled:opacity-50 text-xs"
                          data-testid={`delete-claim-${c.id}`}
                          title="Remove from my list"
                        >
                          {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination */}
            <div className="flex items-center justify-between mt-3 px-2">
              <p className="text-[#5C7599] text-xs">
                Showing <strong className="text-white">{pageStart}–{pageEnd}</strong> of {data.total}
              </p>
              <div className="flex gap-1">
                <button
                  onClick={() => setSkip(Math.max(0, skip - limit))}
                  disabled={skip === 0}
                  className="p-1.5 rounded text-[#A0B4CC] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="claims-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setSkip(skip + limit)}
                  disabled={skip + limit >= data.total}
                  className="p-1.5 rounded text-[#A0B4CC] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="claims-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
